import { ApiError, ApiV1Response, http } from 'api/v1/http'
import axios from 'axios'
import { ZKEX_API_BASE_URL } from 'config'
import {
  AccountId,
  Address,
  Ether,
  L2AccountId,
  L2ChainId,
  Timestamp,
  TokenSymbol,
  UserId,
} from 'types'

export enum ActivateStatus {
  INACTIVE,
  ACTIVATING,
  ACTIVATION_FAILED,
  ACTIVATION_SUCCESS,
}

export interface AccountInfo {
  userId: UserId // zkex user unique ID
  accountId: AccountId // Fund account ID defaults to 0
  layer2Id: L2AccountId // Layer2 account ID
  address: Address
  layer2Address: Address
  pubKey: string
  pubKeyHash: Address
  status: ActivateStatus
  organizationId: 0
  type: null
  level: 0
  internal: boolean
  canSignin: boolean
  canTransfer: boolean
  canTrade: boolean
  canWithdraw: boolean
  authToken: string
  createdAt: number
  updatedAt: number
}
export async function getAccountInfo<
  T extends ApiV1Response<AccountInfo | null>,
>(address: Address, signature?: string, pubKey?: string): Promise<T> {
  return await http
    .get<T>(
      `/users/token/${address}`,
      signature
        ? {
            params: {
              signature,
              pubKey,
            },
          }
        : undefined,
    )
    .then((r) => r.data)
}

export interface GetAccountBalanceData {
  [x: TokenSymbol]: Ether[] // ["10.0", "0"], [available, freeze]
}
export async function getAccountBalance<
  T extends ApiV1Response<GetAccountBalanceData>,
>(
  accountId: number, // 0
): Promise<T> {
  return await http.get(`/trading/${accountId}/balances`).then((r) => r.data)
}

export interface GetAccountAssetsData {
  totalTransferableMargin: number // 42001000000000.00000000000000000000
  totalSpotsAssets: number // 42001000000000.00000000000000000000
  totalAvailableMargin: number // 42001000000000.00000000000000000000
  minimumMaintenanceMargin: number // 0
  contractsUnrealized: number // 0
  contractsPrices: {
    // x: "XBTC"
    [x: string]: number // 42002.607500000000000000,
  }
  priceInfos: {
    // x: "BTC"
    [x: string]: {
      price: number // 42000.00
      conversionRatio: number // 1
    }
  }
  totalMargin: number // 42001000000000.00000000000000000000
  contractsOrders: []
  contractsPositions: {}
  balances: GetAccountBalanceData
  totalUsedMargin: number // 0
  totalFrozenMargin: number // 0
  totalMarginRate: number // 0
  totalNetValue: number // 42001000000000.00000000000000000000
}
export async function getAccountAssets<
  T extends ApiV1Response<GetAccountAssetsData>,
>(
  accountId: number, // 0
): Promise<T> {
  const r = await http.get(`/trading/${accountId}/assets`)
  return r.data
}

interface GetAccountWsTokenData {
  result: string
}
export async function getAccountWsToken<
  T extends ApiV1Response<GetAccountWsTokenData>,
>(): Promise<T> {
  return await http.get(`/users/wss/token`).then((r) => r.data)
}

interface GetAccountNonce {
  nonce: 0 | 2 // 0: Main, 2: Sub-Account
  status: boolean
  nonceType: number
}
export async function getAccountNonce<T extends ApiV1Response<GetAccountNonce>>(
  nonceType: 0 | 2, // 0: Main, 2: Sub-Account
): Promise<T> {
  const r = await http
    .get<T>(`/accountNonce/gen/${nonceType}`)
    .then((r) => r.data)

  if (!r.data.status) {
    throw new ApiError(
      'Sub-Nonce is unavailable. Please try again later or contact us on Discord.',
      100001,
    )
  }
  return r
}

export interface GetFeeParams {
  op: 'Transfer' | 'Withdraw' | 'ChangePubKey'
  address: string
  currency: string
  chainId?: L2ChainId
}
export async function getFee<T extends ApiV1Response<number>>(
  params: GetFeeParams,
): Promise<T> {
  return await http
    .get(`/account/op/fee`, {
      params: {
        ...params,
        currency: params.currency.toUpperCase(),
      },
    })
    .then((r) => r.data)
}

export type TransactionId = number
export interface GetTransactionData {
  txId: TransactionId
  userId: UserId
  accountId: AccountId
  l2AccountId: null | L2AccountId // 如果这个可以有其他数值，你应该替换为实际的类型
  txHash: string
  txType: 'DEPOSIT' | 'CHANGE_PK' | 'TRANSFER' | 'WITHDRAW' | 'FULL_EXIT'
  txStatus: 'PENDING' | 'SUCCESS' | 'FAILED'
  chainId: L2ChainId
  fromAddress: string
  toAddress: string
  currencyId: number
  fee: number
  amount: number
  extra: string
  newPkHash: null | string // 同上，替换为实际的类型如果需要
  nonce: null | number // 同上
  ts: null | number // 同上
  pubKey: null | string // 同上
  signature: null | string // 同上
  ethAuthData: null | string // 同上
  ethSignature: null | string // 同上
  createdAt: number
  updatedAt: number
}
export async function getTransaction<
  T extends ApiV1Response<GetTransactionData>,
>(txId: TransactionId) {
  return await http
    .get<T>(`/account/0/transaction`, {
      params: {
        txId,
      },
    })
    .then((r) => r.data)
    .then((r) => r.data)
}

export interface GetAssetsHistoryParams {
  accountId: number
  recentDays?: number
}
export interface AssetItem {
  amount: number
  createdAt: number
}
export async function getAccountAssetsHistory<
  T extends ApiV1Response<AssetItem[]>,
>(params: GetAssetsHistoryParams): Promise<T> {
  return await http
    .get(`/account/${params.accountId}/assets/stat`, {
      params: {
        recentDays: params.recentDays,
      },
    })
    .then((r) => r.data)
}

export interface UserStatus {
  id: number
  address: Address
  tradeAmount: number
  tradeCount: number
  convertAmount: number
  convertCount: number
  spotAmount: number
  spotCount: number
  depositAmount: number
  depositCount: number
  withdrawAmount: number
  withdrawCount: number
  registerTime: Timestamp
  registerDays: number
  createdAt: Timestamp
}
export async function getUserV2Info<T extends ApiV1Response<UserStatus>>(
  address: string,
): Promise<T> {
  return await axios
    .get(`/users/stat/${address}`, {
      baseURL: ZKEX_API_BASE_URL,
    })
    .then((r) => r.data)
}
